import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Alert, Stack, Chip,
  Avatar, CircularProgress, Stepper, Step, StepLabel, Divider,
} from '@mui/material';
import {
  VerifiedUser, CloudUpload, CheckCircle, Warning, HourglassTop,
  ErrorOutline, Shield, Upload,
} from '@mui/icons-material';
import useAuthStore from '../../store/useStore';
import { kycAPI } from '../../api/client';

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join('; ');
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
}

function KYCGatePage() {
  const { user } = useAuthStore();
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [kycForm, setKycForm] = useState({ id_type: 'ghana_card', id_number: '' });
  const idTypesWithNoBack = ['passport', 'student_id', 'nhis_card'];
  const showIdBack = !idTypesWithNoBack.includes(kycForm.id_type);
  const [kycFiles, setKycFiles] = useState({
    id_front: null, selfie: null, id_back: null, proof_of_address: null,
    student_id: null, admission_letter: null, property_ownership_doc: null,
    business_registration: null,
  });

  useEffect(() => {
    const fetchKyc = async () => {
      try {
        const res = await kycAPI.getMyStatus();
        setKycStatus(res.data);
      } catch (_) {
        setKycStatus(null);
      } finally {
        setLoading(false);
      }
    };
    fetchKyc();
  }, []);

  const handleKycSubmit = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('id_type', kycForm.id_type);
      formData.append('id_number', kycForm.id_number);
      if (kycFiles.id_front) formData.append('id_front', kycFiles.id_front);
      if (kycFiles.selfie) formData.append('selfie', kycFiles.selfie);
      if (kycFiles.id_back) formData.append('id_back', kycFiles.id_back);
      if (kycFiles.proof_of_address) formData.append('proof_of_address', kycFiles.proof_of_address);
      if (kycFiles.student_id) formData.append('student_id', kycFiles.student_id);
      if (kycFiles.admission_letter) formData.append('admission_letter', kycFiles.admission_letter);
      if (kycFiles.property_ownership_doc) formData.append('property_ownership_doc', kycFiles.property_ownership_doc);
      if (kycFiles.business_registration) formData.append('business_registration', kycFiles.business_registration);
      await kycAPI.submit(formData);
      setSuccess('KYC documents submitted successfully! Awaiting admin review.');
      const res = await kycAPI.getMyStatus();
      setKycStatus(res.data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const status = kycStatus?.status || 'not_submitted';
  const activeStep = status === 'not_submitted' ? 0 : status === 'pending' ? 1 : status === 'approved' ? 3 : 0;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4, px: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar sx={{ mx: 'auto', mb: 2, width: 72, height: 72, bgcolor: 'primary.main' }}>
          <Shield sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Identity Verification Required
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
          To ensure safety and reduce fraud on CampusNest, all users must complete KYC verification before accessing their dashboard.
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        <Step><StepLabel>Submit Documents</StepLabel></Step>
        <Step><StepLabel>Under Review</StepLabel></Step>
        <Step><StepLabel>Verified</StepLabel></Step>
      </Stepper>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {status === 'pending' && (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <HourglassTop sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Verification In Progress</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your KYC documents have been submitted and are currently being reviewed by our team.
            This usually takes 1-2 business days.
          </Typography>
          <Chip icon={<Warning />} label="Pending Review" color="warning" sx={{ fontWeight: 600 }} />
        </Paper>
      )}

      {status === 'rejected' && (
        <Paper sx={{ p: 4, borderRadius: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <ErrorOutline sx={{ fontSize: 40, color: 'error.main' }} />
            <Box>
              <Typography variant="h6" fontWeight={700} color="error.main">Verification Rejected</Typography>
              {kycStatus?.rejection_reason && (
                <Typography color="text.secondary">Reason: {kycStatus.rejection_reason}</Typography>
              )}
            </Box>
          </Stack>
          <Alert severity="error" sx={{ mb: 2 }}>
            Your previous submission was rejected. Please review the reason above and resubmit with correct documents.
          </Alert>
        </Paper>
      )}

      {(status === 'not_submitted' || status === 'rejected') && (
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Submit Your KYC Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide a valid government-issued ID and a clear selfie photo. Additional documents may be required based on your role.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="ID Type" value={kycForm.id_type}
                onChange={(e) => setKycForm({ ...kycForm, id_type: e.target.value })}
                SelectProps={{ native: true }}>
                <option value="ghana_card">Ghana Card</option>
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's License</option>
                <option value="student_id">Student ID</option>
                <option value="voters_card">Voter's Card</option>
                <option value="nhis_card">NHIS Card</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="ID Number" value={kycForm.id_number}
                onChange={(e) => setKycForm({ ...kycForm, id_number: e.target.value })}
                placeholder="Enter your ID number" />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Required Documents</Typography></Divider></Grid>

            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                color={kycFiles.id_front ? 'success' : 'primary'}>
                {kycFiles.id_front ? kycFiles.id_front.name : 'ID Front *'}
                <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, id_front: e.target.files[0] })} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                color={kycFiles.selfie ? 'success' : 'primary'}>
                {kycFiles.selfie ? kycFiles.selfie.name : 'Selfie Photo *'}
                <input type="file" hidden accept="image/*" onChange={(e) => setKycFiles({ ...kycFiles, selfie: e.target.files[0] })} />
              </Button>
            </Grid>

            {showIdBack && (
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                  color={kycFiles.id_back ? 'success' : 'inherit'}>
                  {kycFiles.id_back ? kycFiles.id_back.name : 'ID Back (Optional)'}
                  <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, id_back: e.target.files[0] })} />
                </Button>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                color={kycFiles.proof_of_address ? 'success' : 'inherit'}>
                {kycFiles.proof_of_address ? kycFiles.proof_of_address.name : 'Proof of Address (Optional)'}
                <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, proof_of_address: e.target.files[0] })} />
              </Button>
            </Grid>

            {user?.role === 'tenant' && (
              <>
                <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Student Documents</Typography></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                    color={kycFiles.student_id ? 'success' : 'inherit'}>
                    {kycFiles.student_id ? kycFiles.student_id.name : 'Student ID Card'}
                    <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, student_id: e.target.files[0] })} />
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                    color={kycFiles.admission_letter ? 'success' : 'inherit'}>
                    {kycFiles.admission_letter ? kycFiles.admission_letter.name : 'Admission Letter'}
                    <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, admission_letter: e.target.files[0] })} />
                  </Button>
                </Grid>
              </>
            )}

            {(user?.role === 'landlord' || user?.role === 'agent') && (
              <>
                <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Business Documents</Typography></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                    color={kycFiles.property_ownership_doc ? 'success' : 'inherit'}>
                    {kycFiles.property_ownership_doc ? kycFiles.property_ownership_doc.name : 'Property Ownership Doc'}
                    <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, property_ownership_doc: e.target.files[0] })} />
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                    color={kycFiles.business_registration ? 'success' : 'inherit'}>
                    {kycFiles.business_registration ? kycFiles.business_registration.name : 'Business Registration'}
                    <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, business_registration: e.target.files[0] })} />
                  </Button>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button variant="contained" size="large" fullWidth onClick={handleKycSubmit}
                disabled={submitting || !kycForm.id_number || !kycFiles.id_front || !kycFiles.selfie}
                startIcon={submitting ? <CircularProgress size={20} /> : <Upload />}
                sx={{ mt: 1, py: 1.5 }}>
                {submitting ? 'Submitting...' : 'Submit KYC Documents'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
}

export default KYCGatePage;
